import { oc } from "@orpc/contract";
import { z } from "zod";
import { ResourceLeaseHolderSchema } from "./agent-definitions.contract";

const MetadataSchema = z.record(z.string(), z.unknown()).nullable();

export const LeaseableResourceSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  resourceType: z.string(),
  resourceId: z.uuid(),
  maxConcurrent: z.number(),
  leaseTtlSeconds: z.number(),
  isActive: z.boolean(),
  metadata: MetadataSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ResourceLeaseSchema = z.object({
  id: z.uuid(),
  resourceId: z.uuid(),
  idempotencyKey: z.string(),
  lesseeId: z.uuid(),
  status: z.enum(["active", "returned", "expired"]),
  leasedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  returnedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const leaseableResourceContracts = {
  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        resourceType: z.string().min(1).max(100),
        resourceId: z.uuid(),
        maxConcurrent: z.number().int().min(1).optional(),
        leaseTtlSeconds: z.number().int().min(1).optional(),
        isActive: z.boolean().optional(),
        metadata: MetadataSchema.optional(),
      }),
    )
    .output(LeaseableResourceSchema)
    .route({
      summary: "Create a leaseable resource",
      description:
        "Admin-only. Registers a shared resource agents lease concurrently, with a concurrency cap, lease time-to-live, active flag and metadata. Returns the new resource.",
      method: "POST",
    }),
  get: oc
    .input(z.object({ resourceId: z.uuid() }))
    .output(LeaseableResourceSchema.nullable())
    .route({
      summary: "Get a leaseable resource",
      description:
        "Returns one leaseable resource in the organization by id, with its concurrency cap, lease time-to-live and active state, or null if not found.",
      method: "GET",
    }),
  list: oc
    .input(
      z.object({
        resourceType: z.string().optional(),
        isActive: z.boolean().optional(),
        limit: z.number().int().min(1).max(100).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({
        resources: z.array(
          LeaseableResourceSchema.extend({
            /** Active, unexpired leases and the runs holding them; empty when the resource is free. */
            activeLeases: z.array(ResourceLeaseHolderSchema),
          }),
        ),
        total: z.number(),
      }),
    )
    .route({
      summary: "List leaseable resources",
      description:
        "Paginated list of the organization's leaseable resources, filterable by resource type and active state. Each resource carries its active leases with the holding run and that run's status, so a slot kept by a finished run is visible. Returns resources and a total count.",
      method: "GET",
    }),
  update: oc
    .input(
      z.object({
        resourceId: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        maxConcurrent: z.number().int().min(1).optional(),
        leaseTtlSeconds: z.number().int().min(1).optional(),
        isActive: z.boolean().optional(),
        metadata: MetadataSchema.optional(),
      }),
    )
    .output(LeaseableResourceSchema.nullable())
    .route({
      summary: "Update a leaseable resource",
      description:
        "Admin-only. Changes a leaseable resource's name, concurrency cap, lease time-to-live, active flag or metadata. Returns the updated resource, or null if not found.",
      method: "PUT",
    }),
  /**
   * Lift an automatic pause: reactivate the resource and drop the `pause` block from its metadata.
   * Admin-only, like every write on this contract — it is an `update` with a fixed patch, and
   * putting agents back onto a credential is a human's decision. Null when not found.
   */
  resume: oc
    .input(z.object({ resourceId: z.uuid() }))
    .output(LeaseableResourceSchema.nullable())
    .route({
      summary: "Resume a paused leaseable resource",
      description:
        "Admin-only. Reactivates a resource the system paused automatically and clears its pause details, letting agents lease it again. Returns the resource, or null.",
      method: "POST",
    }),
  delete: oc
    .input(z.object({ resourceId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a leaseable resource",
      description:
        "Admin-only. Removes a leaseable resource from the organization. Returns whether it was deleted.",
      method: "DELETE",
    }),
  linkAgent: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        resourceId: z.uuid(),
        isRequired: z.boolean().optional(),
        sortOrder: z.number().int().optional(),
      }),
    )
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Link a leaseable resource to an agent",
      description:
        "Admin-only. Attaches a leaseable resource to an agent definition, optionally marking it required and setting its sort order.",
      method: "POST",
    }),
  unlinkAgent: oc
    .input(z.object({ agentDefinitionId: z.uuid(), resourceId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Unlink a leaseable resource from an agent",
      description:
        "Admin-only. Detaches a leaseable resource from an agent definition. Returns whether a link was removed.",
      method: "DELETE",
    }),
  listForAgent: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(
      z.object({
        resources: z.array(
          LeaseableResourceSchema.extend({
            isRequired: z.boolean(),
            sortOrder: z.number(),
          }),
        ),
      }),
    )
    .route({
      summary: "List leaseable resources for an agent",
      description:
        "Returns the leaseable resources linked to an agent definition, each with whether it is required and its sort order.",
      method: "GET",
    }),
  /**
   * Return every active lease a finished agent run still holds. Admin-only, like every write here.
   * Refused while the run is `running`: a live run must be stopped, not stripped of its desktop.
   */
  releaseRunLeases: oc
    .input(z.object({ agentRunId: z.uuid() }))
    .output(z.object({ released: z.number().int() }))
    .route({
      summary: "Release the leases a finished agent run still holds",
      description:
        "Admin-only. Returns every active lease held by an agent run that is no longer running, after a best-effort close of that run's browser, so agents waiting on the same resources can start. Refused with CONFLICT while the run is still running. Returns how many leases were released.",
      method: "POST",
    }),
  listLeases: oc
    .input(
      z.object({
        resourceId: z.uuid(),
        limit: z.number().int().min(1).max(100).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({ leases: z.array(ResourceLeaseSchema), total: z.number() }),
    )
    .route({
      summary: "List leases on a resource",
      description:
        "Paginated lease history for one leaseable resource: who held it, when leased, expiry, and status (active, returned, expired). Returns leases and total.",
      method: "GET",
    }),
} as const;
